import {
    Box,
    Button,
    Chip,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Typography,
} from '@mui/material';

import { DONE_STATUSES } from '../../../../constants';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import Locale from '/b2b/common/components/Locale';
import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { translate } from '/b2b/common/helpers/i18n';

// Renders the per-tab body of the Chats popover: empty state, conversation
// list, and load-more button. The two tabs share rendering — only their data,
// empty copy, and delete-affordance differ. Backend deletion is currently
// AI-only, so `showDelete` is false for the Privacy Team tab.
const ChatsTabBody = ({
    classes,
    conversations,
    isLoading,
    hasNextPage,
    activeConversationId,
    emptyMessageKey,
    showDelete,
    onSelect,
    onDelete,
    onLoadMore,
}) => {
    if (!isLoading && conversations.length === 0) {
        return (
            <Box className={classes.popoverEmpty}>
                <Typography variant="body1">
                    <Locale path={emptyMessageKey} />
                </Typography>
            </Box>
        );
    }
    return (
        <>
            <List className={classes.popoverList} disablePadding>
                {conversations.map(conversation => {
                    const isActive = conversation.id === activeConversationId;
                    const convIsClosed = DONE_STATUSES.includes(conversation.status);
                    return (
                        <ListItemButton
                            key={conversation.id}
                            className={classNames(
                                classes.conversationItem,
                                isActive && classes.conversationItemActive
                            )}
                            onClick={() => onSelect(conversation.id)}
                        >
                            <ListItemText
                                primary={
                                    <Box className={classes.conversationTitleRow}>
                                        <Typography
                                            variant="body1"
                                            className={classes.conversationTitle}
                                        >
                                            {conversation.title}
                                        </Typography>
                                        {convIsClosed && (
                                            <Chip
                                                className={classes.sidebarChip}
                                                size="small"
                                                variant="outlined"
                                                color="primary"
                                                label={<Locale path="aiAssistant.status.done" />}
                                            />
                                        )}
                                    </Box>
                                }
                                secondary={
                                    <Typography
                                        color="grey.500"
                                        variant="body2"
                                        className={classes.conversationPreview}
                                    >
                                        {moment(
                                            conversation.lastMessageAt || conversation.createdAt
                                        ).format('LLL')}
                                    </Typography>
                                }
                            />
                            {showDelete && (
                                <IconButton
                                    size="small"
                                    color="error"
                                    className={classes.deleteButton}
                                    onClick={event => onDelete(event, conversation)}
                                    aria-label={translate(
                                        'aiAssistant.buttons.deleteChat.ariaLabel'
                                    )}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            )}
                        </ListItemButton>
                    );
                })}
            </List>
            {hasNextPage && (
                <Box className={classes.loadMoreWrapper}>
                    <Button size="small" disabled={isLoading} onClick={onLoadMore}>
                        {isLoading ? (
                            <Locale path="aiAssistant.buttons.loading.label" />
                        ) : (
                            <Locale path="aiAssistant.buttons.loadMore.label" />
                        )}
                    </Button>
                </Box>
            )}
        </>
    );
};

ChatsTabBody.propTypes = {
    classes: PropTypes.object.isRequired,
    conversations: PropTypes.array.isRequired,
    isLoading: PropTypes.bool,
    hasNextPage: PropTypes.bool,
    activeConversationId: PropTypes.string,
    emptyMessageKey: PropTypes.string.isRequired,
    showDelete: PropTypes.bool,
    onSelect: PropTypes.func.isRequired,
    onDelete: PropTypes.func,
    onLoadMore: PropTypes.func,
};

export default ChatsTabBody;
